import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { checkMot } from '../../../ressources/functionJS/checkMot';
import { FiExternalLink } from 'react-icons/fi';
import S from './Widget.module.scss';
import WidgetHeaderControls from './WidgetHeaderControls';
import BibliothequePlotly from '../../Graphe/AffichageGraphe/BibliothequePlotly/BibliothequePlotly';
import Popup from '../../UI/PopupVue/Popup';
import * as actions from '../../../services/actions';

const WidgetGraphe = ({
  graphesFavoris = [],
  headerContent = null,
  showHeader = true,
  loading = false,
}) => {
  const dispatch = useDispatch();
  const donneesGraphes = useSelector(
    (state) => state.graphe.liste_donnees_graphes
  );

  const hasGraphes = graphesFavoris && graphesFavoris.length > 0;
  const [selectedGrapheIndex, setSelectedGrapheIndex] = useState('0');
  const graphLength = graphesFavoris?.length || 0;
  const [afficherPopupGraphe, setAfficherPopupGraphe] = useState(false);
  const ouvrirPopupGraphe = () => setAfficherPopupGraphe(true);
  const fermerPopupGraphe = () => setAfficherPopupGraphe(false);

  useEffect(() => {
    if (!hasGraphes) {
      setSelectedGrapheIndex('0');
      return;
    }

    const currentIndex = Number(selectedGrapheIndex);
    if (currentIndex >= graphLength) {
      setSelectedGrapheIndex('0');
    }
  }, [graphLength, hasGraphes, selectedGrapheIndex]);

  const grapheOptions = useMemo(() => {
    if (!hasGraphes || graphLength < 2) {
      return [];
    }

    return graphesFavoris.map((graphe, index) => ({
      value: String(index),
      label: graphe.graph_name || `${checkMot('graphe')} ${index + 1}`,
    }));
  }, [graphLength, graphesFavoris, hasGraphes]);

  const currentGraphe = hasGraphes
    ? graphesFavoris[Number(selectedGrapheIndex)] || graphesFavoris[0]
    : null;

  // Fetcher les données depuis l'API si absentes du store
  useEffect(() => {
    if (!currentGraphe?.graph_id) return;
    const graphId = parseInt(currentGraphe.graph_id, 10);
    if (!donneesGraphes[graphId]) {
      dispatch(actions.reqRecupererDonneesGraphe(graphId));
    }
  }, [currentGraphe?.graph_id, dispatch]); // eslint-disable-line react-hooks/exhaustive-deps

  // Lire les données depuis le store Redux (données réelles uniquement)
  const storeData = currentGraphe?.graph_id
    ? donneesGraphes[parseInt(currentGraphe.graph_id, 10)] ?? null
    : null;

  // Préparer les données pour BibliothequePlotly
  const plotlyData = useMemo(() => {
    const rawData = storeData?.data ?? currentGraphe?.data;
    if (!rawData || !Array.isArray(rawData)) return [];

    return rawData.map((series, index) => {
      const xData =
        series.x && Array.isArray(series.x)
          ? series.x.map((timestamp) => new Date(timestamp * 1000))
          : [];

      const colors = ['#000000', '#FF6B6B', '#4ECDC4'];

      return {
        x: xData,
        y: series.y || [],
        type: series.type || 'scatter',
        mode: series.mode || 'lines',
        name: series.sensor_name || `Série ${index + 1}`,
        line: {
          color: series.color || colors[index % colors.length],
          dash: series.line?.dash || 'solid',
          shape: series.line?.shape || 'linear',
        },
        hovertemplate:
          '<b>%{fullData.name}</b><br>%{x|%d-%m-%Y %H:%M}<br>%{y:.1f}°C<extra></extra>',
      };
    });
  }, [storeData, currentGraphe]);

  const plotlyLayout = useMemo(() => {
    const config = currentGraphe?.config || {};

    return {
      title: {
        text: config.title || currentGraphe?.graph_name || 'Gráfico',
        subtitle: { text: config.subtitle || '' },
      },
      showlegend: false,
      hovermode: 'x unified',
      xaxis: {
        title: config.xaxis?.title || 'X',
        type: 'date',
        rangeslider: { visible: false },
      },
      yaxis: {
        title: config.yaxis?.title || 'Y',
        type: config.yaxis?.type || 'linear',
      },
      margin: { t: 60, r: 40, b: 40, l: 60 },
    };
  }, [currentGraphe]);

  const resolvedHeader =
    headerContent ||
    (showHeader ? (
      <WidgetHeaderControls
        titleKey="graphe"
        selectOptions={grapheOptions}
        selectedValue={selectedGrapheIndex}
        onSelectChange={setSelectedGrapheIndex}
        headerAction={
          currentGraphe?.graph_id ? (
            <button
              className={S.widgetMenuBtn}
              title={checkMot('voir_plus')}
              onClick={ouvrirPopupGraphe}
            >
              <FiExternalLink size={18} />
            </button>
          ) : null
        }
      />
    ) : null);

  return (
    <>
      <div className={[S.widget, S.widgetMedium].join(' ')}>
        {resolvedHeader}
        <div className={S.widgetContent}>
          {loading && !hasGraphes ? (
            <div className={S.widgetLoadingState}>
              <div className={S.widgetLoadingSpinner} />
              <span className={S.widgetLoadingText}>{checkMot('chargement_en_cours')}</span>
            </div>
          ) : hasGraphes && currentGraphe && plotlyData.length > 0 ? (
            <div className={S.grapheContainer}>
              <BibliothequePlotly
                data={plotlyData}
                layout={plotlyLayout}
                dimensionWidth={
                  window.innerWidth <= 1024 ? window.innerWidth - 100 : 400
                }
                dimensionHeight={window.innerWidth <= 1024 ? 300 : 250}
                minimaliste={false}
              />
            </div>
          ) : (
            <div className={S.emptyState}>
              <p className={S.emptyText}>{checkMot('aucun_graphe_favori')}</p>
            </div>
          )}
        </div>
      </div>
      <Popup
        sansBouton={true}
        eventALaFermeture={fermerPopupGraphe}
        showModal={afficherPopupGraphe}
      >
        {afficherPopupGraphe && currentGraphe && (
          <BibliothequePlotly
            data={plotlyData}
            layout={plotlyLayout}
            dimensionWidth={window.innerWidth - 100}
            dimensionHeight={window.innerHeight - 200}
            minimaliste={false}
          />
        )}
      </Popup>
    </>
  );
};

export default WidgetGraphe;
